import React, { useState, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from "@mui/material";
import {
    Email as EmailIcon,
    CheckCircle as CheckCircleIcon,
    Block as BlockIcon,
    Warning as WarningIcon,
    Delete as DeleteIcon,
    PowerSettingsNew as DisableIcon,
    CheckCircleOutline as ReactivateIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import { ApiGet, ApiPut, ApiDelete } from "../../../helpers/API/ApiData";
import { toast } from "react-toastify";
import { usePermissions } from "../../../hooks/usePermissions";

interface AdminUser {
    _id: string;
    adminName: string;
    emailId: string;
    phoneNo: string;
    status: {
        name: string;
    };
    role?: {
        name: string;
        vDescription?: string;
    };
    createdAt: string;
}

interface AdminUserListMUIProps {
    refreshTrigger?: number;
}

export const AdminUserListMUI: React.FC<AdminUserListMUIProps> = ({
    refreshTrigger = 0,
}) => {
    const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        adminId?: string;
        adminName?: string;
        action?: "disable" | "delete" | "reactivate";
    }>({ open: false });

    // Permission checks for admin user management
    const { canView, canEdit } = usePermissions('/userAdmin');

    useEffect(() => {
        fetchAdminUsers();
    }, [refreshTrigger]);

    const fetchAdminUsers = async () => {
        setLoading(true);
        try {
            const response: any = await ApiGet("admin/get-admin?page=1&limit=100");
            if (response?.data?.data) {
                // Map the response data to match our interface
                const mappedData = response.data.data.map((admin: any) => ({
                    _id: admin._id,
                    adminName: admin.adminName,
                    emailId: admin.email,
                    phoneNo: admin.mobileno,
                    status: admin.status || { name: "active" },
                    role: admin.roleId || null,
                    createdAt: admin.createdAt,
                }));
                setAdminUsers(mappedData);
            }
        } catch (error: any) {
            console.error("Error fetching admin users:", error);
            toast.error("Fehler beim Laden der Administratoren");
        } finally {
            setLoading(false);
        }
    };

    const handleResendEmail = async (adminId: string) => {
        try {
            const response: any = await ApiGet(`admin/resend-welcome-email?id=${adminId}`);
            if (response?.status === 200) {
                toast.success("Willkommens-E-Mail wurde erfolgreich erneut gesendet");
            } else {
                toast.error(response?.data?.message || "Fehler beim Senden der E-Mail");
            }
        } catch (error: any) {
            toast.error(
                error?.response?.data?.message ||
                "Fehler beim Senden der E-Mail. Bitte versuche es erneut"
            );
        }
    };

    const handleDisableAdmin = (adminId: string, adminName: string) => {
        setConfirmDialog({
            open: true,
            adminId,
            adminName,
            action: "disable",
        });
    };

    const handleDeleteAdmin = (adminId: string, adminName: string) => {
        setConfirmDialog({
            open: true,
            adminId,
            adminName,
            action: "delete",
        });
    };

    const handleReactivateAdmin = (adminId: string, adminName: string) => {
        setConfirmDialog({
            open: true,
            adminId,
            adminName,
            action: "reactivate",
        });
    };

    const handleConfirmAction = async () => {
        if (!confirmDialog.adminId) return;

        try {
            if (confirmDialog.action === "disable") {
                const response: any = await ApiPut(
                    `admin/active-and-blocked?id=${confirmDialog.adminId}`,
                    { status: "blocked" }
                );
                if (response?.status === 200 || response?.data?.success) {
                    toast.success("Administrator erfolgreich deaktiviert");
                    fetchAdminUsers();
                }
            } else if (confirmDialog.action === "reactivate") {
                const response: any = await ApiPut(
                    `admin/active-and-blocked?id=${confirmDialog.adminId}`,
                    { status: "active" }
                );
                if (response?.status === 200 || response?.data?.success) {
                    toast.success("Administrator erfolgreich reaktiviert");
                    fetchAdminUsers();
                }
            } else if (confirmDialog.action === "delete") {
                const response: any = await ApiDelete(
                    `admin/delete-admin?id=${confirmDialog.adminId}`
                );
                if (response?.status === 200 || response?.data?.success) {
                    toast.success("Administrator erfolgreich gelöscht");
                    fetchAdminUsers();
                }
            }
            setConfirmDialog({ open: false });
        } catch (error: any) {
            toast.error(
                error?.response?.data?.message ||
                "Fehler beim Verarbeiten der Aktion. Bitte versuche es erneut"
            );
        }
    };

    const getStatusChip = (status: string) => {
        switch (status.toLowerCase()) {
            case "active":
                return (
                    <Chip
                        label="Aktiv"
                        size="small"
                        icon={<CheckCircleIcon sx={{ color: "inherit" }} />}
                        color="success"
                    />
                );
            case "inactive":
                return (
                    <Chip
                        label="Ausstehend"
                        size="small"
                        icon={<WarningIcon sx={{ color: "inherit" }} />}
                        color="warning"
                    />
                );
            case "blocked":
                return (
                    <Chip
                        label="Blockiert"
                        size="small"
                        icon={<BlockIcon sx={{ color: "inherit" }} />}
                        color="error"
                    />
                );
            default:
                return <Chip label={status} size="small" />;
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (adminUsers.length === 0) {
        return (
            <Box sx={{ padding: 4, textAlign: "center", color: (theme) => theme.palette.text.secondary }}>
                <Typography variant="h6" gutterBottom>
                    Keine Administratoren gefunden
                </Typography>
                <Typography variant="body2">
                    Erstellen Sie Ihren ersten Administrator über die Schaltfläche oben
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ open: false })}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title" sx={{ color: "error.main", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {confirmDialog.action === "disable"
                        ? "Administrator deaktivieren?"
                        : confirmDialog.action === "reactivate"
                            ? "Administrator reaktivieren?"
                            : "Administrator löschen?"}
                    <IconButton size="small" onClick={() => setConfirmDialog({ open: false })} aria-label="close">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {confirmDialog.action === "disable"
                            ? `Möchten Sie den Administrator "${confirmDialog.adminName}" wirklich deaktivieren?`
                            : confirmDialog.action === "reactivate"
                                ? `Möchten Sie den Administrator "${confirmDialog.adminName}" wirklich reaktivieren?`
                                : `Möchten Sie den Administrator "${confirmDialog.adminName}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmDialog({ open: false })}
                        color="inherit"
                    >
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleConfirmAction}
                        variant="contained"
                        color={
                          confirmDialog.action === "delete"
                            ? "error"
                            : confirmDialog.action === "reactivate"
                              ? "success"
                              : "warning"
                        }
                    >
                        {confirmDialog.action === "disable"
                            ? "Deaktivieren"
                            : confirmDialog.action === "reactivate"
                                ? "Reaktivieren"
                                : "Löschen"}
                    </Button>
                </DialogActions>
            </Dialog>
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>E-Mail</TableCell>
                            <TableCell>Telefon</TableCell>
                            <TableCell>Rolle</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Aktionen</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {adminUsers.map((admin) => (
                            <TableRow key={admin._id}>
                                <TableCell>{admin.adminName}</TableCell>
                                <TableCell>{admin.emailId}</TableCell>
                                <TableCell>{admin.phoneNo}</TableCell>
                                <TableCell>
                                    {admin.role?.name ? (
                                        <Chip
                                            label={admin.role.name}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ) : (
                                        <Typography variant="caption" color="textSecondary">
                                            Keine Rolle
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>{getStatusChip(admin.status?.name || "active")}</TableCell>
                                <TableCell align="center">
                                    {admin.status?.name?.toLowerCase() === "inactive" && canEdit && (
                                        <Tooltip title="Willkommens-E-Mail erneut senden">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleResendEmail(admin._id)}
                                            >
                                                <EmailIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {canEdit && (
                                        <>
                                            {admin.status?.name?.toLowerCase() === "active" && (
                                                <Tooltip title="Deaktivieren">
                                                    <IconButton
                                                        size="small"
                                                        color="warning"
                                                        onClick={() =>
                                                            handleDisableAdmin(admin._id, admin.adminName)
                                                        }
                                                    >
                                                        <DisableIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {admin.status?.name?.toLowerCase() === "blocked" && (
                                                <Tooltip title="Reaktivieren">
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        onClick={() =>
                                                            handleReactivateAdmin(admin._id, admin.adminName)
                                                        }
                                                    >
                                                        <ReactivateIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Löschen">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() =>
                                                        handleDeleteAdmin(admin._id, admin.adminName)
                                                    }
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                    {!canEdit && (
                                        <Typography variant="caption" color="textSecondary">
                                            Keine Berechtigung
                                        </Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};
